/**
 * Timeslots represents availabilityExceptions in LiveRoomPage
 * as we couldn't use listings availabilityPlan to achieve the
 * timeslots logic as we wanted.
 */
import React, { useState } from 'react';
import {
  AVAILABILITY_EXCEPTION_METADATA_ACCESS_MEMBERS,
  AVAILABILITY_EXCEPTION_METADATA_ACCESS_TIER,
} from '../../util/types';
import { FormattedMessage } from '../../util/reactIntl';
import { getDefaultTimeZoneOnBrowser } from '../../util/dates';
import { Button, SecondaryButton, IconSpinner, Icon } from '..';

import TimeRange from './TimeRange';
import css from './BookingPanel.module.css';

const MAX_VISIBLE_TIMESLOTS = 3;

const defaultTimeZone = () =>
  typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

// As our UI only shows maximum of three timeslots at once,
// we need to slice timeslots and allow user to view more
// by clicking the button.
const visibleTimeslots = (timeslots, length) => timeslots.slice(0, length);

const Timeslot = props => {
  const {
    timeslot,
    timeslotMetadata,
    timeslotsMetadata,
    timeZone,
    hasFreeMemberships,
    hasPaidMemberships,
    currentUserMemberships,
    membershipTiers,
    isOwnListing,
    onOpenMembershipModal,
    onBooking,
    onOpenFreeMembershipModal,
    formValues,
    bookedTimeslotIds,
    reserveEpisodeInProgressId,
    reserveEpisodeErrorId,
    highlightTimeslotId,
  } = props;

  // Check if this timeslot is already booked by the user
  const isBooked = bookedTimeslotIds?.includes(timeslot.id.uuid);

  const {
    title,
    access,
    membershipTierIds,
    membershipTierId,
    meeting,
    timeZone: ownTimeZone,
    mainExceptionId,
  } = timeslotMetadata?.attributes || {}; // Additional details of the single timeslot (e.g availabilityException metadata)
  // Repeats of a series were saved without a time zone; theirs is the first
  // episode's.
  const bookingTimeZone =
    ownTimeZone ||
    timeslotsMetadata?.find(m => m.id === mainExceptionId)?.attributes?.timeZone ||
    timeZone;
  const { start, end } = timeslot.attributes;

  // Supports both new membershipTierIds (array) and legacy membershipTierId (single)
  const selectedTierIds = membershipTierIds || (membershipTierId ? [membershipTierId] : []);

  const membersOnlyTimeslot = access === AVAILABILITY_EXCEPTION_METADATA_ACCESS_MEMBERS;
  const tierSpecificTimeslot = access === AVAILABILITY_EXCEPTION_METADATA_ACCESS_TIER;

  // Get required memberships for tier-specific timeslots
  const requiredMemberships = selectedTierIds
    .map(id => membershipTiers?.find(m => m.id === id))
    .filter(Boolean);
  const requiredMembership = requiredMemberships[0] || null;

  // Check if user has any of the required tiers
  const userHasRequiredTier =
    selectedTierIds.length > 0
      ? currentUserMemberships?.some(m => selectedTierIds.includes(m.id))
      : false;

  const userHasOtherMembership = currentUserMemberships?.length > 0 && !userHasRequiredTier;

  const timeslotParams = {
    id: timeslot.id.uuid,
    name: title,
    access,
    start: new Date(start),
    end: new Date(end),
    meeting,
    quantity: formValues.quantity,
    timeZone: bookingTimeZone,
  };

  /**
   * Handles the action when joining a timeslot.
   * - For tier-specific timeslots, check if user has the required tier
   * - For members-only timeslots, prompt for payment if the user does not have a paid membership.
   * - For free timeslots, prompt for free membership if user has no membership.
   * - Otherwise, proceed with the booking.
   */
  const handleJoinTimeslot = () => {
    if (tierSpecificTimeslot) {
      if (userHasRequiredTier || selectedTierIds.length === 0) {
        // User has required tier or no specific tier required, proceed with booking
        onBooking({ timeslot: timeslotParams });
      } else {
        // User needs specific tier membership
        onOpenMembershipModal({
          timeslot: timeslotParams,
          requiredMembership,
          isUpgrade: userHasOtherMembership,
        });
      }
    } else if (membersOnlyTimeslot && !hasPaidMemberships) {
      // User needs a paid membership to join this timeslot.
      onOpenMembershipModal({ timeslot: timeslotParams });
    } else if (
      !membersOnlyTimeslot &&
      !tierSpecificTimeslot &&
      !hasPaidMemberships &&
      !hasFreeMemberships
    ) {
      // Free timeslot but user does not have any membership.
      onOpenFreeMembershipModal({ timeslot: timeslotParams });
    } else {
      // User has the necessary membership or it's a non-restricted timeslot.
      onBooking({ timeslot: timeslotParams });
    }
  };

  const renderStartDate = (
    <TimeRange startDate={start} endDate={end} dateType="date" timeZone={timeZone} />
  );
  const renderTimes = (
    <TimeRange startDate={start} endDate={end} dateType="time" timeZone={timeZone} />
  );

  const getButtonText = () => {
    return <FormattedMessage id="BookingPanel.join" />;
  };

  const timeslotDisabled = isOwnListing;
  const isLocked =
    (tierSpecificTimeslot && !userHasRequiredTier) || (membersOnlyTimeslot && !hasPaidMemberships);

  return (
    <div
      key={timeslot.id.uuid}
      className={`${css.timeslot} ${isLocked ? css.timeslotLocked : ''} ${
        highlightTimeslotId === timeslot.id.uuid ? css.timeslotHighlighted : ''
      }`}
    >
      <div className={css.timeslotInfo}>
        <span className={css.timeslotTitle}>
          {tierSpecificTimeslot && requiredMembership ? (
            <Icon name="premium" className={css.premiumIcon} />
          ) : tierSpecificTimeslot && !requiredMembership ? (
            <Icon name="premium" className={css.premiumIcon} />
          ) : membersOnlyTimeslot ? (
            <Icon name="premium" className={css.premiumIcon} />
          ) : null}
          <span>{title || renderStartDate}</span>
        </span>
        <span className={css.timeslotDates}>
          {tierSpecificTimeslot && requiredMembership ? (
            <FormattedMessage
              id="BookingPanel.timeslotDatesTierSpecific"
              values={{
                membershipName: requiredMembership.name,
                date: renderStartDate,
                time: renderTimes,
              }}
            />
          ) : tierSpecificTimeslot && !requiredMembership ? (
            <FormattedMessage
              id="BookingPanel.timeslotDatesDeletedTier"
              values={{
                deletedMembershipText: <FormattedMessage id="BookingPanel.deletedMembership" />,
                date: renderStartDate,
                time: renderTimes,
              }}
            />
          ) : (
            <FormattedMessage
              id="BookingPanel.timeslotDatesRegular"
              values={{
                date: renderStartDate,
                time: renderTimes,
              }}
            />
          )}
        </span>
      </div>
      {isBooked ? (
        <span className={css.bookedBadge}>
          <FormattedMessage id="BookingPanel.booked" />
        </span>
      ) : (
        <div className={css.timeslotAction}>
          <Button
            className={`${css.timeslotButton} ${isLocked ? css.upgradeButton : ''}`}
            type="button"
            onClick={handleJoinTimeslot}
            disabled={timeslotDisabled}
            inProgress={reserveEpisodeInProgressId === timeslot.id.uuid}
          >
            {getButtonText()}
          </Button>
          {reserveEpisodeErrorId === timeslot.id.uuid ? (
            <p className={css.reserveError}>
              <FormattedMessage id="BookingPanel.reserveError" />
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

const PanelTimeslots = props => {
  // Maintain visible timeslots that currently renders
  // three timeslots at once, and allows users to see
  // more by clicking a button.
  const {
    timeslots,
    timeslotsMetadata,
    currentListing,
    fetchInProgress,
    fetchError,
    ...rest
  } = props;

  // A /go/rsvp link points at one episode; make sure it is among those shown.
  const [maxVisibleTimeslots, setMaxVisibleTimeslots] = useState(() => {
    const index = (timeslots || []).findIndex(t => t.id.uuid === props.highlightTimeslotId);
    return Math.max(MAX_VISIBLE_TIMESLOTS, index + 1);
  });

  // Get membership tiers from listing
  const membershipTiers = currentListing?.attributes?.publicData?.memberships || [];
  const timeslotCount = timeslots ? timeslots.length : 0;

  if (fetchInProgress) {
    return (
      <div className={css.panelTimeslots}>
        <IconSpinner className={css.spinner} />
      </div>
    );
  } else if (fetchError) {
    return (
      <div className={css.panelTimeslots}>
        <p className={css.error}>
          <FormattedMessage id="BookingPanel.fetchError" />
        </p>
      </div>
    );
  } else if (timeslotCount === 0) {
    return (
      <div className={css.panelTimeslots}>
        <p className={css.noTimeslots}>
          <FormattedMessage id="BookingPanel.noTimeslots" />
        </p>
      </div>
    );
  }

  return (
    <div className={css.panelTimeslots}>
      <div className={css.timeslots}>
        {visibleTimeslots(timeslots, maxVisibleTimeslots).map(t => {
          const timeslotMetadata = timeslotsMetadata?.find(
            metadata =>
              metadata.id === t.id.uuid ||
              metadata.attributes?.repeatingExceptionIds?.includes(t.id.uuid)
          );
          return (
            <Timeslot
              key={t.id.uuid}
              timeslot={t}
              timeslotMetadata={timeslotMetadata}
              timeslotsMetadata={timeslotsMetadata}
              timeZone={defaultTimeZone()}
              membershipTiers={membershipTiers}
              {...rest}
            />
          );
        })}
      </div>
      {timeslots.length > maxVisibleTimeslots ? (
        <SecondaryButton
          className={css.showMoreTimeslotsButton}
          onClick={() => setMaxVisibleTimeslots(timeslots.length)}
        >
          <FormattedMessage id="BookingPanel.showMoreTimeslots" />
        </SecondaryButton>
      ) : null}
    </div>
  );
};

export default PanelTimeslots;
